import { resolveContentLink } from "@/lib/contentLink";
import { useRef, useState, useEffect, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { PageContainer } from "@/components/layouts/MainLayout";
import { T } from "@/components/editable/T";

export interface ResourceCard {
  href: string;
  image: string;
  alt: string;
  type: "blog" | "webinar" | "template" | "eguide";
  titleKey: string;
  titleDefault: string;
}

const typeBadgeStyles: Record<string, { bg: string; text: string; border: string; label: string }> = {
  blog: { bg: "bg-white/90", text: "text-orange-600", border: "border border-orange-200", label: "Blog" },
  webinar: { bg: "bg-white/90", text: "text-amber-600", border: "border border-amber-200", label: "Webinar" },
  template: { bg: "bg-white/90", text: "text-primary", border: "border border-primary/20", label: "Template" },
  eguide: { bg: "bg-white/90", text: "text-primary", border: "border border-primary/20", label: "E-guide" },
};

const resourceHubPathByLang = {
  nl: "/hr-knowledge-hub",
  en: "/hr-knowledge-hub",
  de: "/hr-wissenszentrum",
} as const;

const resourceDetailPathByType: Record<ResourceCard["type"], string> = {
  blog: "/blog",
  webinar: "/webinar",
  template: "/template",
  eguide: "/eguide",
};

const resourceCardOverrides: Record<string, { image: string; href?: string; alt?: string }> = {
  "functieprofiel-opstellen": { image: "/images/eguide-functieprofiel-hero.png" },
  "competentiematrix-skills-matrix": { image: "/images/blog/hero-20.png" },
  "competenties-voorbeelden": { image: "/images/resources/competentieset-soft-skills.webp" },
  "learned-engagement-model": { image: "/images/blog/mto-vragenlijst-hero.avif" },
  "moderne-hr-cyclus-tips": { image: "/images/blog/hero-06.png" },
  "transparant-belonen-styr": { image: "/images/resources/webinar-transparant-belonen.png" },
  "functioneringsgesprek": { image: "/images/blog/hero-16.png" },
  "voorbeelden-ai-hrm": { image: "/images/template-ai-prompts-hero.png" },
  "integratie-learned-rakoo": { image: "/images/resources/rakoo-learned-webinar.webp" },
  "functioneringsgesprek-nieuwe-stijl": { image: "/images/blog/template-evaluatiegesprek-hero.avif" },
  "ai-in-performance-management": { image: "/images/resources/ai-evaluatieproces-masterclass.webp" },
  "9-grid-model": { image: "/images/blog/hero-08.png" },
  "performance-management-cyclus": {
    image: "/images/blog/hero-04.png",
    href: "/blog/de-ultieme-gids-voor-performance-management",
  },
  "de-ultieme-gids-voor-performance-management": { image: "/images/blog/hero-04.png" },
};

const stripLangPrefix = (path: string) => path.replace(/^\/(?:nl|en|de)(?=\/|$)/, "");

const getCardSlug = (path: string) => {
  const normalizedPath = stripLangPrefix(path.split(/[?#]/)[0] || "");
  const segments = normalizedPath.split("/").filter(Boolean);
  return segments.at(-1) || "";
};

const getCardOverride = (path: string) => resourceCardOverrides[getCardSlug(path)];

interface PageResourcesProps {
  page: string;
  cards: ResourceCard[];
  titleDefault?: string;
  subtitleDefault?: string;
}

export function PageResources({
  page,
  cards,
  titleDefault = "Handige resources",
  subtitleDefault = "Gebruik onze blogs, templates en webinars om van experts te leren.",
}: PageResourcesProps) {
  const { lang } = useParams<{ lang: string }>();
  const currentLang = (lang || "nl") as "nl" | "en" | "de";
  const badge = typeBadgeStyles;
  const scrollRef = useRef<HTMLDivElement>(null);

  // Webinars are NL-only; filter them out on EN/DE pages
  const filteredCards = useMemo(
    () => {
      const filtered = lang && lang !== "nl" ? cards.filter((c) => c.type !== "webinar") : cards;
      return filtered.slice(0, 3);
    },
    [cards, lang],
  );

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 30);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 30);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    // Delay initial check to ensure layout is complete
    const timer = setTimeout(updateScrollState, 100);
    el.addEventListener("scroll", updateScrollState, { passive: true });
    window.addEventListener("resize", updateScrollState);
    const ro = new ResizeObserver(updateScrollState);
    ro.observe(el);
    return () => {
      clearTimeout(timer);
      el.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
      ro.disconnect();
    };
  }, [updateScrollState, filteredCards]);

  const scroll = (direction: "left" | "right") => {
    if (!scrollRef.current) return;
    const containerWidth = scrollRef.current.clientWidth;
    scrollRef.current.scrollBy({
      left: direction === "left" ? -containerWidth : containerWidth,
      behavior: "smooth",
    });
  };

  const prefixWithLang = useCallback((path: string) => {
    if (/^(https?:|mailto:|tel:|#)/.test(path)) return path;
    if (/[?#]/.test(path)) return path;
    return resolveContentLink(path, currentLang);
  }, [currentLang]);

  const getResolvedCard = useCallback((card: ResourceCard) => {
    const href = card.href?.trim() || "";
    const override = getCardOverride(href);
    return {
      href: href || override?.href || "",
      image: override?.image ?? card.image,
      alt: override?.alt ?? card.alt,
    };
  }, []);

  const getCardHref = useCallback((card: ResourceCard) => {
    const href = (getResolvedCard(card).href || "").trim();
    const legacyHubDetailMatch = href.match(/^\/(?:hr-kenniscentrum|hr-knowledge-hub|hr-wissenszentrum)\/([^/?#]+)/);

    if (legacyHubDetailMatch) {
      const [, slug] = legacyHubDetailMatch;
      return prefixWithLang(`${resourceDetailPathByType[card.type]}/${slug}`);
    }

    if (["/hr-kenniscentrum", "/hr-knowledge-hub", "/hr-wissenszentrum"].includes(href)) {
      return prefixWithLang(resourceHubPathByLang[currentLang]);
    }

    return prefixWithLang(href);
  }, [currentLang, getResolvedCard, prefixWithLang]);

  const viewAllHref = prefixWithLang(resourceHubPathByLang[currentLang]);

  return (
    <section className="pt-[100px] pb-20 px-6">
      <PageContainer>
        <div className="text-center mb-12">
          <h2 className="feature-section-heading mb-4 text-foreground"><T page={page} k="resources.title" d={titleDefault} /></h2>
          <T page={page} k="resources.subtitle" d={subtitleDefault} as="p" className="text-lead text-muted-foreground max-w-2xl mx-auto" />
        </div>
        <div className="relative">
          {canScrollLeft && <button
            onClick={() => scroll("left")}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-background border border-border shadow-md flex items-center justify-center hover:bg-muted transition-colors md:hidden"
          >
            <ChevronLeft className="w-5 h-5 text-foreground" />
          </button>}
          {canScrollRight && <button
            onClick={() => scroll("right")}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-background border border-border shadow-md flex items-center justify-center hover:bg-muted transition-colors md:hidden"
          >
            <ChevronRight className="w-5 h-5 text-foreground" />
          </button>}
        <div ref={scrollRef} className="flex gap-0 overflow-x-auto snap-x snap-mandatory pb-4 -mx-6 md:mx-0 md:grid md:grid-cols-3 md:gap-8 md:overflow-visible md:pb-0" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          {filteredCards.map((card, i) => {
            const b = badge[card.type];
            const bgColors = ["#F3EDFD", "#FFFBEB", "#FDF2ED"];
            const bgColor = bgColors[i % bgColors.length];
            const resolvedCard = getResolvedCard(card);
            return (
               <a key={i} href={getCardHref(card)} className="group flex-shrink-0 w-[calc(100vw-90px)] mx-6 snap-center md:w-auto md:mx-0 rounded-2xl overflow-hidden hover:shadow-lg transition-all duration-300 block border-[10px] border-white/70" style={{ backgroundColor: bgColor }}>
                <div className="aspect-[16/10] overflow-hidden rounded-xl">
                  <img src={resolvedCard.image} alt={resolvedCard.alt} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="p-6">
                  <span className={`inline-block px-3 py-1 text-xs font-medium rounded-full ${b.bg} ${b.text} ${b.border} mb-3`}>
                    <T page={page} k={`resources.categories.${card.type}`} d={b.label} />
                  </span>
                  <T page={page} k={card.titleKey} d={card.titleDefault} as="p" className="text-base font-semibold text-foreground group-hover:text-primary transition-colors" />
                </div>
              </a>
            );
          })}
        </div>
        </div>
        <div className="text-center mt-12">
          <Button variant="outline" size="lg" className="rounded-full px-8" asChild>
            <a href={viewAllHref}>
              <T page={page} k="resources.viewAll" d="Bekijk alle resources" />
              <ArrowRight className="ml-2 h-4 w-4" />
            </a>
          </Button>
        </div>
      </PageContainer>
    </section>
  );
}
